import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  timer,
  exhaustMap,
  takeUntil,
  takeWhile,
  tap,
  finalize,
  map,
  distinctUntilChanged,
  EMPTY,
  catchError,
  of,
  forkJoin,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  isVendorSyncComplete,
  mapOsintJobPollResultsFromResponse,
  toPercent,
} from './osint-job-poll-response.util';

/** @deprecated Prefer isVendorSyncComplete — kept as local alias for readability. */
const isOsintJobVendorSyncComplete = isVendorSyncComplete;
import { OsintSearchService } from '../osint-search/osint-search.service';
import { LeakSearchService } from '../leak-search/leak-search.service';
import { PanSearchService } from '../pan/pan.service';
import { GstSearchService } from '../gst/gst.service';
import { DinSearchService } from '../din/din.service';
import { MobilePanSearchService } from '../mobile-pan/mobile-pan.service';
import { PhonepeFastagSearchService } from '../phonepe-fastag/phonepe-fastag.service';
import { PhonepeUpiSearchService } from '../phonepe-upi/phonepe-upi.service';
import { VehicleSearchService } from '../vehicle/vehicle.service';
import { AdvancedSearchService } from '../advanced/advanced-search.service';
import { BasicSearchService } from '../basic-search/basic-search.service';

export interface BackgroundPollJob {
  job_id: string;
  searchType: string;
  label?: string;
  progress_percent?: number | null;
  progress_milestone?: number | null;
  done?: boolean;
  vendor_sync_complete?: boolean;
  background_refresh_pending?: boolean;
  status?: string;
  emails_ready?: boolean;
  found?: number | null;
  vendor_status?: string | null;
  is_final?: boolean;
  deleted?: boolean;
  timed_out?: boolean;
  /** Epoch ms when this job first entered background polling. */
  started_at?: number;
  progress?: { checked?: number | null; total?: number | null; percent?: number | null } | null;
}

@Injectable({ providedIn: 'root' })
export class SearchPollingService {
  private jobResultsMap = new Map<string, any>();
  private jobResultsSubject = new BehaviorSubject<Map<string, any>>(new Map());
  jobResults$ = this.jobResultsSubject.asObservable();

  private pollingSessions = new Map<string, Subscription>();

  /** Jobs tracked across route changes until vendor sync completes. */
  private trackedJobs = new Map<string, BackgroundPollJob>();
  private trackedJobsSubject = new BehaviorSubject<BackgroundPollJob[]>([]);
  activeBackgroundJobs$ = this.trackedJobsSubject.asObservable();

  private backgroundLoopSub?: Subscription;
  private backgroundPollIntervalMs = 3000;
  private readonly STORAGE_KEY = 'osint_bg_poll_jobs';
  /** Stop username (and other bg) polls after 20 minutes from start. */
  private readonly POLL_TIMEOUT_MS = 20 * 60 * 1000;

  constructor(
    private toastr: ToastrService,
    private osintSearchService: OsintSearchService,
    private leakSearchService: LeakSearchService,
    private panSearchService: PanSearchService,
    private gstSearchService: GstSearchService,
    private dinSearchService: DinSearchService,
    private mobilePanSearchService: MobilePanSearchService,
    private phonepeFastagSearchService: PhonepeFastagSearchService,
    private phonepeUpiSearchService: PhonepeUpiSearchService,
    private vehicleSearchService: VehicleSearchService,
    private advancedSearchService: AdvancedSearchService,
    private basicSearchService: BasicSearchService,
  ) {
    this.restoreTrackedJobsFromStorage();
  }

  /**
   * Keep polling these jobs in the root service until each is vendor-complete.
   * Survives leaving the search / result page. Dedupes by job_id.
   */
  ensureBackgroundPolling(
    jobs: Array<{ job_id: string; searchType: string; label?: string }>,
    pollInterval = 3000,
  ) {
    if (!jobs?.length) {
      return;
    }

    this.backgroundPollIntervalMs = pollInterval || this.backgroundPollIntervalMs;
    const now = Date.now();

    for (const job of jobs) {
      if (!job?.job_id) continue;
      const existing = this.trackedJobs.get(job.job_id);
      if (existing && isOsintJobVendorSyncComplete(existing)) {
        continue;
      }
      this.trackedJobs.set(job.job_id, {
        ...(existing || {}),
        job_id: job.job_id,
        searchType: job.searchType || existing?.searchType || 'username',
        label: job.label ?? existing?.label,
        done: existing?.done ?? false,
        vendor_sync_complete: false,
        status: existing?.status || 'pending',
        progress_percent: existing?.progress_percent ?? null,
        progress_milestone: existing?.progress_milestone ?? null,
        started_at: existing?.started_at || now,
        deleted: false,
        timed_out: false,
      });
    }

    this.emitTrackedJobs();
    this.persistTrackedJobs();
    this.startBackgroundLoop();
  }

  /** Stop tracking jobs (e.g. after user deletes history rows). */
  stopTrackingJobIds(jobIds: string[]) {
    if (!jobIds?.length) return;
    let changed = false;
    for (const id of jobIds) {
      if (!id) continue;
      const key = String(id);
      if (this.trackedJobs.delete(key)) {
        changed = true;
      }
      if (this.jobResultsMap.delete(key)) {
        changed = true;
      }
    }
    if (!changed) return;
    this.jobResultsSubject.next(new Map(this.jobResultsMap));
    this.emitTrackedJobs();
    this.persistTrackedJobs();
  }

  getActiveBackgroundJobs(): BackgroundPollJob[] {
    return this.trackedJobsSubject.value;
  }

  startPolling(
    jobIds: string[],
    fetchResultsFn: (jobIds: string[]) => Observable<any>,
    pollDuration = 3 * 60 * 1000,
    pollInterval = 5 * 1000,
  ) {
    const sessionId = Date.now().toString();

    let activeJobIds = [...jobIds];
    const completedJobIds = new Set<string>();

    // First poll immediately so cache-fast-path jobs get a vendor status without waiting pollInterval.
    // exhaustMap: never cancel an in-flight /osint/job (live vendor GET can exceed the interval).
    const polling$ = timer(0, pollInterval).pipe(
      takeUntil(timer(pollDuration)),
      exhaustMap(() => {
        if (activeJobIds.length === 0) return EMPTY;
        return fetchResultsFn(activeJobIds).pipe(
          catchError((error) => {
            // Handle error response that may contain data structure
            if (error?.error?.data) {
              const errorData = error.error.data;
              const results = mapOsintJobPollResultsFromResponse({
                data: errorData,
              });

              results.forEach((result: any) => {
                this.jobResultsMap.set(result.job_id, result);
                if (isOsintJobVendorSyncComplete(result)) {
                  completedJobIds.add(result.job_id);
                }
              });

              this.jobResultsSubject.next(new Map(this.jobResultsMap));

              activeJobIds = activeJobIds.filter(
                (job_id) => !completedJobIds.has(job_id),
              );

              return of(null); // Return empty to continue polling
            }
            // Transient / aborted: skip this tick, keep polling
            return of(null);
          }),
        );
      }),
      tap((response) => {
        if (!response) return; // Skip if it was an error response we handled

        const results = mapOsintJobPollResultsFromResponse(response);

        results.forEach((result: any) => {
          if (
            result.done === true &&
            result.status === 'failed' &&
            !result.notes
          ) {
            result.status = 'completed';
          }
          this.jobResultsMap.set(result.job_id, result);
          if (isOsintJobVendorSyncComplete(result)) {
            completedJobIds.add(result.job_id);
          }
        });

        this.jobResultsSubject.next(new Map(this.jobResultsMap));

        activeJobIds = activeJobIds.filter(
          (job_id) => !completedJobIds.has(job_id),
        );
      }),
      takeWhile(() => activeJobIds.length > 0, true),
      finalize(() => {
        this.pollingSessions.delete(sessionId);
        if (activeJobIds.length > 0) {
          console.log(`❌ Some jobs got timed out in session ${sessionId}.`);
        }
      }),
    );

    const sub = polling$.subscribe({
      next: () => {},
      error: () => {},
      complete: () => {},
    });

    this.pollingSessions.set(sessionId, sub);
  }

  getJobResult(jobId: string): Observable<any> {
    return this.jobResults$.pipe(
      map((jobMap) => jobMap.get(jobId)),
      distinctUntilChanged(),
    );
  }

  getAllJobResults(): Observable<Map<string, any>> {
    return this.jobResults$;
  }

  checkJobStatus(
    jobIds: string[],
    fetchResultsFn: (jobIds: string[]) => Observable<any>,
  ): Observable<Map<string, any>> {
    if (!jobIds || jobIds.length === 0) {
      return new Observable((observer) => {
        observer.next(new Map(this.jobResultsMap));
        observer.complete();
      });
    }

    return fetchResultsFn(jobIds).pipe(
      tap((response) => {
        const results = mapOsintJobPollResultsFromResponse(response);

        results.forEach((result: any) => {
          if (
            result.done === true &&
            result.status === 'failed' &&
            !result.notes
          ) {
            result.status = 'completed';
          }
          this.jobResultsMap.set(result.job_id, result);
        });

        this.jobResultsSubject.next(new Map(this.jobResultsMap));
      }),
      catchError((error) => {
        // Handle error response that may contain data structure
        if (error?.error?.data) {
          const errorData = error.error.data;
          const results = mapOsintJobPollResultsFromResponse({
            data: errorData,
          });

          results.forEach((result: any) => {
            this.jobResultsMap.set(result.job_id, result);
          });

          this.jobResultsSubject.next(new Map(this.jobResultsMap));

          // Return successful observable with updated map so component's next handler processes it
          return of(new Map(this.jobResultsMap));
        }

        // If no data in error, rethrow so component's error handler can handle it
        throw error;
      }),
      map(() => new Map(this.jobResultsMap)),
    );
  }

  getCachedJobStatus(jobId: string): any {
    return this.jobResultsMap.get(jobId) || null;
  }

  reset() {
    this.pollingSessions.forEach((sub) => sub.unsubscribe());
    this.pollingSessions.clear();
    this.backgroundLoopSub?.unsubscribe();
    this.backgroundLoopSub = undefined;
    this.trackedJobs.clear();
    this.emitTrackedJobs();
    this.clearPersistedTrackedJobs();
    this.jobResultsMap.clear();
    this.jobResultsSubject.next(new Map());
  }

  private startBackgroundLoop() {
    if (this.backgroundLoopSub && !this.backgroundLoopSub.closed) {
      return;
    }
    if (!this.hasPendingTrackedJobs()) {
      return;
    }

    const interval = this.backgroundPollIntervalMs || 3000;

    this.backgroundLoopSub = timer(0, interval)
      .pipe(
        // exhaustMap avoids canceling in-flight polls (live vendor GET can exceed interval).
        exhaustMap(() => {
          this.expireTimedOutJobs();

          const pending = [...this.trackedJobs.values()].filter(
            (j) => !isOsintJobVendorSyncComplete(j),
          );
          if (!pending.length) {
            return EMPTY;
          }

          const byType = new Map<string, string[]>();
          for (const job of pending) {
            const type = job.searchType || 'username';
            const ids = byType.get(type) || [];
            ids.push(job.job_id);
            byType.set(type, ids);
          }

          const requests = [...byType.entries()].map(([type, ids]) =>
            this.fetchBackgroundJobs(type, ids).pipe(
              catchError((error) => {
                if (error?.error?.data) {
                  return of({ data: error.error.data });
                }
                // Not-found / deleted style 4xx with no data: stop those ids.
                if (error?.status === 400 || error?.status === 404) {
                  return of({
                    data: {
                      deleted: true,
                      status: 'deleted',
                      job_ids: ids,
                      jobs: ids.map((job_id) => ({
                        job_id,
                        deleted: true,
                        status: 'deleted',
                        vendor_sync_complete: true,
                        result_ready: false,
                      })),
                    },
                  });
                }
                return of(null);
              }),
            ),
          );

          return forkJoin(requests);
        }),
        tap((responses) => {
          if (!responses) return;
          for (const response of responses) {
            if (!response) continue;
            this.applyPollResults(mapOsintJobPollResultsFromResponse(response));
          }
        }),
        takeWhile(() => this.hasPendingTrackedJobs(), true),
        finalize(() => {
          this.backgroundLoopSub = undefined;
        }),
      )
      .subscribe({
        next: () => {},
        error: () => {
          this.backgroundLoopSub = undefined;
          // Retry loop shortly so a transient failure does not abandon jobs
          setTimeout(() => this.startBackgroundLoop(), interval);
        },
      });
  }

  /**
   * Route background polls to the correct API by searchType.
   * Leak types use `leak-*` keys so they do not collide with OSINT phone/email/etc.
   */
  private fetchBackgroundJobs(type: string, ids: string[]): Observable<any> {
    const key = String(type || '').toLowerCase();

    if (
      key === 'phone' ||
      key === 'email' ||
      key === 'username' ||
      key === 'name' ||
      key === 'wallet'
    ) {
      return this.osintSearchService.getSearchResults(key, ids);
    }

    if (key === 'leak-phone' || key === 'advanced-leak-phone') {
      return this.leakSearchService.getSearchResults('phone', ids);
    }
    if (key === 'leak-email' || key === 'advanced-leak-email') {
      return this.leakSearchService.getSearchResults('email', ids);
    }
    if (key === 'leak-username') {
      return this.leakSearchService.getSearchResults('username', ids);
    }
    if (key === 'leak-password') {
      return this.leakSearchService.getSearchResults('password', ids);
    }

    if (key === 'pan') {
      return this.pollSingleIdJobs(ids, (id) =>
        this.panSearchService.getSearchResults([id]),
      );
    }
    if (key === 'gst') {
      return this.gstSearchService.getSearchResults(ids);
    }
    if (key === 'din') {
      return this.dinSearchService.getSearchResults(ids);
    }
    if (key === 'mobile_pan' || key === 'mobile-pan') {
      return this.mobilePanSearchService.getSearchResults(ids);
    }
    if (key === 'phonepe_fastag' || key === 'phonepe-fastag') {
      return this.phonepeFastagSearchService.getSearchResults(ids);
    }
    if (key === 'phonepe_upi' || key === 'phonepe-upi') {
      return this.phonepeUpiSearchService.getSearchResults(ids);
    }
    if (key === 'vehicle') {
      return this.pollSingleIdJobs(ids, (id) =>
        this.vehicleSearchService.getSearchResults([id]),
      );
    }
    if (key === 'advanced') {
      return this.pollSingleIdJobs(ids, (id) =>
        this.advancedSearchService.getSearchResults([id]),
      );
    }

    if (key === 'basic' || key === 'basic-phone') {
      return this.pollSingleIdJobs(ids, (id) =>
        this.basicSearchService.getStatus(id).pipe(
          map((response) => this.mapBasicStatusToPollResponse(id, response)),
        ),
      );
    }

    // Unknown type: try OSINT endpoint as a last resort.
    return this.osintSearchService.getSearchResults(key, ids);
  }

  /** Adapt GET /basic/status/:id into the shared poll jobs shape. */
  private mapBasicStatusToPollResponse(trackedId: string, response: any) {
    const data = response?.data || {};
    const done = Boolean(data.done);
    const refreshPending = Boolean(data.background_refresh_pending);
    const vendorSyncComplete = isVendorSyncComplete({
      done,
      background_refresh_pending: refreshPending,
      status: data.status,
      vendor_sync_complete: data.vendor_sync_complete,
    });
    const platformJobs =
      data.platform_jobs && typeof data.platform_jobs === 'object'
        ? data.platform_jobs
        : {};
    const entries = Object.values(platformJobs);
    const terminal = new Set([
      'completed',
      'failed',
      'error',
      'timeout',
      'cancelled',
    ]);
    let percent: number | null = toPercent(
      data?.progress?.percent,
      data.progress_percent,
    );
    if (vendorSyncComplete) {
      percent = 100;
    } else if (percent == null && entries.length) {
      const finished = entries.filter((job: any) =>
        terminal.has(String(job?.status || '').toLowerCase()),
      ).length;
      // Always expose a percent while platforms are known so the panel keeps
      // refreshing instead of looking "stuck" with no progress value.
      percent = Math.round((finished / entries.length) * 100);
    }

    const status = vendorSyncComplete
      ? 'completed'
      : done && refreshPending
        ? 'refreshing'
        : 'pending';

    return {
      data: {
        jobs: [
          {
            job_id: trackedId,
            done,
            result_ready: Boolean(data.has_result) || done,
            vendor_sync_complete: vendorSyncComplete,
            background_refresh_pending: refreshPending,
            basic_search_id: data.basic_search_id || trackedId,
            status,
            progress_percent: percent,
            progress: { percent },
            is_final: vendorSyncComplete,
          },
        ],
      },
    };
  }

  /** PAN / vehicle / advanced APIs accept one job_id per request. */
  private pollSingleIdJobs(
    ids: string[],
    fetchOne: (id: string) => Observable<any>,
  ): Observable<any> {
    const uniqueIds = [...new Set((ids || []).filter(Boolean))];
    if (!uniqueIds.length) {
      return of({ data: { jobs: [] } });
    }

    const fetchOneSafe = (id: string) =>
      fetchOne(id).pipe(
        catchError((error) => {
          if (error?.error?.data) {
            return of({ data: error.error.data });
          }
          if (error?.status === 400 || error?.status === 404) {
            return of({
              data: {
                deleted: error?.status === 404,
                status: error?.status === 404 ? 'deleted' : 'failed',
                done: true,
                job_ids: [id],
                jobs: [
                  {
                    job_id: id,
                    deleted: error?.status === 404,
                    status: error?.status === 404 ? 'deleted' : 'failed',
                    done: true,
                    vendor_sync_complete: true,
                    background_refresh_pending: false,
                    result_ready: false,
                    notes: error?.error?.message_key || error?.error?.message,
                  },
                ],
              },
            });
          }
          return of(null);
        }),
      );

    if (uniqueIds.length === 1) {
      return fetchOneSafe(uniqueIds[0]);
    }

    return forkJoin(uniqueIds.map((id) => fetchOneSafe(id))).pipe(
      map((responses) => {
        const jobs = (responses || [])
          .filter(Boolean)
          .flatMap((response) => mapOsintJobPollResultsFromResponse(response));
        return { data: { jobs } };
      }),
    );
  }

  /** Mark jobs that exceeded 20 minutes from poll start as error and stop them. */
  private expireTimedOutJobs() {
    const now = Date.now();
    let changed = false;

    for (const [id, job] of [...this.trackedJobs.entries()]) {
      if (isOsintJobVendorSyncComplete(job)) continue;
      const startedAt = Number(job.started_at) || now;
      if (now - startedAt < this.POLL_TIMEOUT_MS) continue;

      const timedOut: BackgroundPollJob = {
        ...job,
        status: 'error',
        timed_out: true,
        done: true,
        vendor_sync_complete: true,
        is_final: true,
      };
      this.trackedJobs.set(id, timedOut);
      this.jobResultsMap.set(id, {
        ...(this.jobResultsMap.get(id) || {}),
        ...timedOut,
        job_id: id,
      });
      changed = true;

      const label = job.label || job.job_id;
      this.toastr.error(
        `Search timed out after 20 minutes for ${label}`,
        'Search error',
      );
    }

    if (!changed) return;

    // Drop timed-out jobs from active tracking after notifying UI via jobResults$
    for (const [id, job] of [...this.trackedJobs.entries()]) {
      if (job.timed_out) {
        this.trackedJobs.delete(id);
      }
    }
    this.jobResultsSubject.next(new Map(this.jobResultsMap));
    this.emitTrackedJobs();
    this.persistTrackedJobs();
  }

  private applyPollResults(results: any[]) {
    let completedAny = false;

    results.forEach((result: any) => {
      if (!result?.job_id) return;

      if (
        result.deleted !== true &&
        result.status !== 'deleted' &&
        result.done === true &&
        result.status === 'failed' &&
        !result.notes
      ) {
        result.status = 'completed';
      }

      this.jobResultsMap.set(result.job_id, result);

      const tracked = this.trackedJobs.get(result.job_id);
      if (tracked) {
        const wasComplete = isOsintJobVendorSyncComplete(tracked);
        const deleted =
          result.deleted === true || result.status === 'deleted';
        const progressPercent =
          toPercent(result?.progress?.percent, result.progress_percent) ??
          tracked.progress_percent ??
          null;

        const refreshPending =
          result.background_refresh_pending === true ||
          result.status === 'refreshing';
        const next: BackgroundPollJob = {
          ...tracked,
          done: deleted ? true : Boolean(result.done),
          status: deleted
            ? 'deleted'
            : isOsintJobVendorSyncComplete(result)
              ? 'completed'
              : result.status === 'error' || result.status === 'failed'
                ? result.status
                : refreshPending
                  ? 'refreshing'
                  : 'pending',
          deleted,
          progress_percent: progressPercent,
          progress_milestone:
            result.progress_milestone ?? tracked.progress_milestone,
          emails_ready: Boolean(result.emails_ready),
          found: result.found ?? tracked.found,
          vendor_status: result.vendor_status ?? tracked.vendor_status,
          is_final: deleted
            ? true
            : Boolean(
                result.is_final === true ||
                  isOsintJobVendorSyncComplete(result),
              ),
          vendor_sync_complete: isOsintJobVendorSyncComplete(result),
          background_refresh_pending: deleted
            ? false
            : Boolean(
                result.background_refresh_pending === true ||
                  result.status === 'refreshing',
              ),
          progress: result.progress ?? tracked.progress ?? null,
          started_at: tracked.started_at,
        };
        this.trackedJobs.set(result.job_id, next);

        if (!wasComplete && isOsintJobVendorSyncComplete(next)) {
          completedAny = true;
          const label = next.label || next.job_id;
          if (deleted) {
            this.toastr.info(
              `Search removed for ${label}`,
              'Search deleted',
            );
          } else if (
            next.timed_out ||
            next.status === 'error' ||
            next.status === 'failed'
          ) {
            // Timeout toast already shown in expireTimedOutJobs
          } else {
            this.toastr.success(
              next.searchType === 'username'
                ? `Username search finished for ${label}`
                : next.searchType === 'basic' || next.searchType === 'basic-phone'
                  ? `Basic search finished for ${label}`
                  : `Search finished for ${label}`,
              'Search complete',
            );
          }
        }
      }
    });

    this.jobResultsSubject.next(new Map(this.jobResultsMap));

    // Drop completed / deleted jobs from the active panel after notifying
    if (completedAny) {
      for (const [id, job] of [...this.trackedJobs.entries()]) {
        if (isOsintJobVendorSyncComplete(job)) {
          this.trackedJobs.delete(id);
        }
      }
    }

    this.emitTrackedJobs();
    this.persistTrackedJobs();
  }

  private hasPendingTrackedJobs(): boolean {
    for (const job of this.trackedJobs.values()) {
      if (!isOsintJobVendorSyncComplete(job)) {
        return true;
      }
    }
    return false;
  }

  private emitTrackedJobs() {
    const pending = [...this.trackedJobs.values()].filter(
      (j) => !isOsintJobVendorSyncComplete(j),
    );
    this.trackedJobsSubject.next(pending);
  }

  private persistTrackedJobs() {
    try {
      const payload = [...this.trackedJobs.values()]
        .filter((j) => !isOsintJobVendorSyncComplete(j))
        .map((j) => ({
          job_id: j.job_id,
          searchType: j.searchType,
          label: j.label,
          started_at: j.started_at || Date.now(),
        }));
      if (!payload.length) {
        sessionStorage.removeItem(this.STORAGE_KEY);
        return;
      }
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }
  }

  private clearPersistedTrackedJobs() {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  private restoreTrackedJobsFromStorage() {
    try {
      const raw = sessionStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || !parsed.length) return;

      const now = Date.now();
      const stillValid = parsed
        .filter((j) => j?.job_id && j?.searchType)
        .filter((j) => {
          const started = Number(j.started_at) || now;
          return now - started < this.POLL_TIMEOUT_MS;
        });

      if (!stillValid.length) {
        this.clearPersistedTrackedJobs();
        return;
      }

      for (const j of stillValid) {
        const jobId = String(j.job_id);
        this.trackedJobs.set(jobId, {
          job_id: jobId,
          searchType: String(j.searchType),
          label: j.label ? String(j.label) : undefined,
          started_at: Number(j.started_at) || now,
          done: false,
          vendor_sync_complete: false,
          status: 'pending',
        });
      }
      this.emitTrackedJobs();
      this.persistTrackedJobs();
      this.startBackgroundLoop();
    } catch {
      this.clearPersistedTrackedJobs();
    }
  }
}
